import { Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Direction, DxIdVersion, InsightKind } from '@fullstory/proto/fs/api/proactiveinsights';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import {
  InsightDetail,
  InsightTrendGroupByDetail,
  InsightTrendGroupByWords,
  InsightTrendWords,
  isInsightTrendDetail,
  isInsightTrendGroupByDetail,
  isInsightTrendGroupByWords,
  isInsightTrendWords,
} from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import {
  FromDashboardQueryParams,
  generateRoutePath,
  METRIC_INSIGHT_ROUTE,
} from '@fullstory/routes';
import React, { ReactNode } from 'react';
import { FormatDateOptions } from 'react-intl';
import { useSelector } from 'react-redux';
import { useInsightsForMetric } from './useInsightsForMetric';
import { addDays, compareAsc, subDays } from 'date-fns';

interface MostRecentInsightOptions {
  end?: Date;
  /**
   * Set to false to disable.
   *
   * @default true
   */
  enabled?: boolean;
}

export function useMostRecentInsight(
  dxIdVersion: DxIdVersion,
  opts: MostRecentInsightOptions = {},
  queryParams?: FromDashboardQueryParams,
) {
  const { end = new Date(), enabled = true } = opts;
  const orgId = useSelector(getOrgId);
  const insights = useInsightsForMetric(dxIdVersion, enabled);

  // get the most recently computed insight
  const mostRecentInsight = insights.reduce(
    (recent, curr) =>
      Timestamp.toMillis(curr.computedAt) > Timestamp.toMillis(recent?.computedAt) ? curr : recent,
    insights[0],
  );

  if (!mostRecentInsight) return null;

  // filter out insight if it is no longer relevant
  const insightEndDate = Timestamp.toDate(mostRecentInsight.end);
  if (!insightEndDate) return null;

  const expiryDate =
    mostRecentInsight.kind === InsightKind.INSIGHT_KIND_MONTH_OVER_MONTH
      ? addDays(insightEndDate, 30)
      : addDays(insightEndDate, 7);
  const insightStart =
    mostRecentInsight.kind === InsightKind.INSIGHT_KIND_MONTH_OVER_MONTH
      ? subDays(insightEndDate, 30)
      : subDays(insightEndDate, 7);
  if (compareAsc(end, expiryDate) > 0 || compareAsc(end, insightStart) < 0) {
    return null;
  }

  const insight = generateOneLiner(mostRecentInsight);
  const insightRoute = generateRoutePath(
    METRIC_INSIGHT_ROUTE,
    {
      orgId,
      id: dxIdVersion.id,
      insightId: mostRecentInsight.insightId,
    },
    queryParams,
  );

  return { insight, insightRoute };
}

function generateOneLiner(insightDetail: InsightDetail) {
  const { insightWords, trend, start, end } = insightDetail;
  const dateFormat: FormatDateOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  const evaluatedRangeStart = Timestamp.mustDate(start);
  const evaluatedRangeEnd = Timestamp.mustDate(end);

  const trendInsightRangeStart = Timestamp.toDate(
    insightDetail.trend?.periodicStats[insightDetail.trend.periodicStats.length - 1]?.startDate,
  );
  const groupByInsightRangeStart = Timestamp.toDate(
    (insightDetail.trendGroup?.periodicTables[insightDetail.trendGroup.periodicTables.length - 1]
      ?.aggregateTable?.metricsDataTable?.table ?? [])[0]?.startTime,
  );

  const startDate =
    isInsightTrendDetail(insightDetail) && trendInsightRangeStart
      ? trendInsightRangeStart
      : isInsightTrendGroupByDetail(insightDetail) && groupByInsightRangeStart
      ? groupByInsightRangeStart
      : evaluatedRangeStart;
  const endDate = subDays(evaluatedRangeEnd, 1);

  const intlDateString = `${intl.formatDate(startDate, dateFormat)} - ${intl.formatDate(
    endDate,
    dateFormat,
  )}`;

  const multipleProperties = isInsightTrendGroupByDetail(insightDetail)
    ? isMultiProp(insightDetail)
    : false;

  if (isInsightTrendWords(insightWords)) {
    return getTrendInsightOneLiner(insightWords, trend?.ratioChange ?? 0, intlDateString);
  } else if (isInsightTrendGroupByWords(insightWords)) {
    return getTrendGroupByInsightOneLiner(insightWords, intlDateString, multipleProperties);
  }
  return null;
}

function getTrendInsightOneLiner(
  insightWords: InsightTrendWords,
  ratioChange: number,
  dateRangeString: string,
) {
  if (!isFinite(ratioChange)) {
    // for NaN case
    return intl.formatMessage(
      {
        defaultMessage:
          'The {isMonthly, select, true{monthly} other{weekly}} {isVolatilityTrend, select, true{fluctuation} other{average count}} has {isIncrease, select, true{increased above zero} other{decreased to zero}}.',
        id: '/Mvl2Y',
      },
      {
        isIncrease: insightWords.isIncrease,
        isMonthly: insightWords.isMonthly,
        isVolatilityTrend: insightWords.isVolatilityTrend,
      },
    );
  }
  return intl.formatMessage(
    {
      defaultMessage:
        'The <t>{isMonthly, select, true{monthly} other{weekly}}</t> {isVolatilityTrend, select, true{fluctuation} other{average count}} has {isIncrease, select, true{increased} other{decreased}}{hasRatioChange, select, true { {ratioChange}} other {}}.',
      id: 'ISasNU',
    },
    {
      t: (children: ReactNode) => (
        <Tooltip data-component="getTrendInsightOneLiner" tooltipContent={dateRangeString} asChild>
          <span style={{ borderBottom: '1px dashed' }}>{children}</span>
        </Tooltip>
      ),
      isMonthly: insightWords.isMonthly,
      isIncrease: insightWords.isIncrease,
      isVolatilityTrend: insightWords.isVolatilityTrend,
      hasRatioChange: !!insightWords.ratioChangeI18l,
      ratioChange: insightWords.ratioChangeI18l,
    },
  );
}

function getTrendGroupByInsightOneLiner(
  insightWords: InsightTrendGroupByWords,
  dateRangeString: string,
  multipleProperties: boolean,
) {
  return intl.formatMessage(
    {
      defaultMessage:
        'The <t>{isMonthly, select, true{monthly} other{weekly}}</t> count from the leading {multipleProperties, select, true{properties} other{property}} has {isIncrease, select, true{increased} other{decreased}} by {topValueRatioChange}.',
      id: 'fpg9Tm',
    },
    {
      t: (children: ReactNode) => (
        <Tooltip
          data-component="getTrendGroupByInsightOneLiner"
          tooltipContent={dateRangeString}
          asChild
        >
          <span style={{ borderBottom: '1px dashed' }}>{children}</span>
        </Tooltip>
      ),
      isMonthly: false, // group by insights are currently always weekly. leaving this here in case we support monthly group by insights in the future
      isIncrease: insightWords.topValueIsIncrease,
      topValueRatioChange: insightWords.topValueRatioChange,
      multipleProperties,
    },
  );
}

function isMultiProp(insight: InsightTrendGroupByDetail) {
  const periodicTables = insight.trendGroup.periodicTables;
  const feat = insight.trendGroup.topValueChange ?? insight.trendGroup.gapChange;
  const targetTableIdx =
    feat?.direction == Direction.DIRECTION_DECREASING ? 0 : periodicTables.length - 1;
  const knee = periodicTables[targetTableIdx]?.kneeIndex;
  return knee !== 1;
}
